/**
 * Created by piotr.pozniak@thebeaverhead.com on 15/12/2020.
 */

import React, { useContext, useMemo } from "react";
import PropTypes from "prop-types";
import classnames from "classnames";
import CardEvent from "./CardEvent";
import InfiniteScrollWrapper from "../InfiniteScrollWrapper";
import { CT, PT } from "../../../../consts";
import PoweredBy from "../PoweredBy";
import CalendarHeader from "../CalendarHeader";
import NoEvents from "../NoEvents";
import SearchInput from "../AiSearch/SearchInput";
import useAiSearch from "../../../../hooks/useAiSearch";
import WidgetSettingsContext from "../../../../contexts/WidgetSettingsContext";
import LoaderIndicator from "./LoaderIndicator";
import { useTranslation } from "react-i18next";

/**
 * Splits a chronologically-sorted event collection into "upcoming/ongoing"
 * (start_date >= now, or ongoing: start_date < now < end_date) followed by
 * "past" (start_date < now), with past events ordered most-recent-first.
 * Returns both the reordered collection and the index where "Past Events"
 * should be inserted (-1 if there are no past events to show).
 */
const partitionUpcomingPast = (collection) => {
  const nowSeconds = Date.now() / 1000;

  const upcoming = [];
  const past = [];

  for (const event of collection) {
    const isPast =
      event.start_time < nowSeconds &&
      !(event.end_time && event.end_time > nowSeconds);

    (isPast ? past : upcoming).push(event);
  }

  // upcoming/ongoing: soonest first
  upcoming.sort((a, b) => a.start_date - b.start_date);
  // past: most recent first
  past.sort((a, b) => b.start_date - a.start_date);

  return {
    collection: [...upcoming, ...past],
    firstPastIndex: past.length ? upcoming.length : -1,
  };
};

const CardView = (props) => {
  const { calendar, events, topMatches } = props;
  const widgetSettings = useContext(WidgetSettingsContext);
  const { t } = useTranslation();

  const { hasAIEnabled } = useAiSearch(calendar.model.integration);
  const loading =
    calendar.rsvp ||
    calendar.fetch ||
    calendar.delete ||
    calendar.update ||
    events.fetch;

  const showAsList = calendar.model && calendar.model.template == "card_list";

  const inpicDatePlacement = widgetSettings["cwDateLocation"];
  const showDate = Number.parseInt(widgetSettings["ccShowDate"]) === 1;
  const selectedDateTimeFormat = widgetSettings["ccShowTimeOptions"];

  const cardsPerRow =
    (widgetSettings["cwColsCount"] ? widgetSettings["cwColsCount"] : "").split(
      "1fr"
    ).length - 1;

  const hasResponsiveGrid =
    Number.parseInt(widgetSettings["cwResponsiveCards"]) === 1;

  const showTitle = Number.parseInt(widgetSettings["ccShowTitle"]) === 1;
  const showLocation = Number.parseInt(widgetSettings["ccShowLocation"]) === 1;

  const showDescription =
    Number.parseInt(widgetSettings["ccShowDescription"]) === 1;

  const descriptionClamp = widgetSettings["ccDescriptionLength"];

  const showSummary = Number.parseInt(widgetSettings["ccShowSummary"]) === 1;
  const hoverEffect = widgetSettings["cwHoverEffect"];
  const showWeekDayInList =
    Number.parseInt(widgetSettings["eventDetailsShowDayOfWeek"]) === 1;
  const weekDayFormatter = widgetSettings["eventsShowDayWeekOption"];

  const useEventAccent =
    Number.parseInt(widgetSettings["generalUseEventAccent"]) === 1;
  const showImage =
    Number.parseInt(widgetSettings["eventDetailsShowImage"]) == 1;
  const coverAspectRatio = widgetSettings["ccImageAspectRatio"];

  const hasInfiniteScroll =
    Number.parseInt(widgetSettings["infiniteScroll"]) === 1;

  const recurringShowBadge =
    Number.parseInt(widgetSettings["recurringShowBadge"]) === 1;
  const featuredShowBadge =
    Number.parseInt(widgetSettings["featuredShowBadge"]) === 1;

  const badgesLocation = widgetSettings["recurringBadgeLocation"];

  const showCardCta = Number.parseInt(widgetSettings["cCardShowCta1st"]) === 1;
  const cCardCtaButtonFullWidth =
    Number.parseInt(widgetSettings["cCardCtaButtonFullWidth"]) === 1;

  const bestResultsLabel = widgetSettings["aiBestMatchesLabel"];
  const bestRemainingResultsLabel = widgetSettings["aiRemainingResultsLabel"];
  const showImageContainer =
    Number.parseInt(widgetSettings["eventDetailsShowImageContainer"]) === 1;

  const triggerCTAOnCardClick =
    Number.parseInt(widgetSettings["cCardActionOnClick"]) === 1;

  const showAccentColorOnCard =
    Number.parseInt(widgetSettings["cwShowAccentBar"]) === 1;

  const showTags = Number.parseInt(widgetSettings["ccShowTags"]) === 1;
  const showTagsAsPills =
    Number.parseInt(widgetSettings["ccShowTagsAsPills"]) === 1;
  const hasTagsLimit = Number.parseInt(widgetSettings["ccLimitTags"]) === 1;
  const showTagSign = Number.parseInt(widgetSettings["ccShowSign"]) === 1;
  const tagsLimit = hasTagsLimit
    ? Number.parseInt(widgetSettings["ccLimitTagsNumber"])
    : "all";
  const rejectedTags = widgetSettings["ccRejectedTags"]
    ?.split(",")
    .map((tag) =>
      (tag.startsWith("#") ? tag.splice(1, undefined) : tag).trim()
    );

  const isTimeRangeMode = widgetSettings["eventsNumberOption"] === "date_range";
  const sortPastBelow =
    widgetSettings["dateRangeSortOrderOption"] === "upcoming-first";
  const shouldPartition = isTimeRangeMode && sortPastBelow;

  const areCardsEqualHeight =
    Number.parseInt(widgetSettings["ccCardEqHeight"]) === 1;

  const watermark =
    calendar.model && calendar.model.is_watermarked ? (
      <PoweredBy align={hasInfiniteScroll ? "right" : "left"} />
    ) : null;

  const calendarID = "dce_calendar__" + calendar.model.uuid;

  const cardCreator = (i, idx) => {
    return (
      <CardEvent
        key={i.slug}
        event={i}
        inpicDatePlacement={inpicDatePlacement}
        showDate={showDate}
        showTitle={showTitle}
        showLocation={showLocation}
        showDescription={showDescription}
        showSummary={showSummary}
        hoverEffect={hoverEffect}
        embedded={props.embedded}
        showWeekDay={showWeekDayInList}
        weekDayFormatter={weekDayFormatter}
        useEventAccent={useEventAccent}
        showImage={showImage}
        coverAspectRatio={coverAspectRatio}
        showRecurringBadge={recurringShowBadge}
        showFeaturedBadge={featuredShowBadge}
        badgesLocation={badgesLocation}
        showCardCta={showCardCta}
        selectedDateTimeFormat={selectedDateTimeFormat}
        showAsList={showAsList}
        showImageContainer={showImageContainer}
        cCardCtaButtonFullWidth={cCardCtaButtonFullWidth}
        triggerCTAOnCardClick={triggerCTAOnCardClick}
        showAccentOnCard={showAccentColorOnCard}
        showTags={showTags}
        numberOfTags={tagsLimit}
        showTagSign={showTagSign}
        showTagsAsPills={showTagsAsPills}
        rejectedTags={rejectedTags}
        descriptionClamp={descriptionClamp}
        areCardsEqualHeight={areCardsEqualHeight}
      />
    );
  };

  const { collection: sortedCollection, firstPastIndex } = useMemo(() => {
    if (!shouldPartition) {
      return { collection: events.collection, firstPastIndex: -1 };
    }
    return partitionUpcomingPast(events.collection);
  }, [events.collection, shouldPartition]);

  const cardEvents = sortedCollection.map((event, idx) => (
    <React.Fragment key={event.slug}>
      {idx === firstPastIndex && (
        <h3 className="card-view--past-events-header">{t("Past events")}</h3>
      )}
      {cardCreator(event, idx)}
    </React.Fragment>
  ));

  // const cardEvents = events.collection.map(cardCreator);

  const topMatchingEvents = events.topMatchesCollection
    ? events.topMatchesCollection.slice(0, cardsPerRow).map(cardCreator)
    : [];

  const loadingIndicator = loading ? <LoaderIndicator /> : null;

  const noEvents = !loading && !events.collection.length ? <NoEvents /> : null;

  const search =
    hasAIEnabled && widgetSettings["aiEnabled"] ? (
      <SearchInput
        searchEvents={props.searchEvents}
        fetchEvents={props.fetchEvents}
      />
    ) : null;

  const topMatchesContent = topMatchingEvents.length ? (
    <React.Fragment>
      <div className={"card-view--top-matches-container"}>
        <h3 className={"card-view--top-matches-container_header"}>
          {bestResultsLabel}
        </h3>
      </div>
      <div
        className={classnames("card-view", {
          "card-view-horizontal": showAsList,
        })}
      >
        {topMatchingEvents}
      </div>
    </React.Fragment>
  ) : null;

  // return <LoaderIndicator />;
  //console.log('has more', events.collection.length, events.totalRows, events.collection.length != events.totalRows)
  return (
    <div className={classnames("calendar-card-view", `dce--${CT.card}`)}>
      <CalendarHeader
        disabled={events.fetch}
        calendar={calendar}
        currentDate={props.selectedDate}
        onChangeDate={props.onSelectedDateChange}
        events={events.collection}
        locations={events.locations}
        onUpcomingNavigationChange={props.onUpcomingNavigationChange}
        selectedFilters={props.selectedFilters}
        onChangeFilter={props.onChangeFilter}
        onSearch={props.onKeywordSearch}
      />

      {search}
      {noEvents}

      {topMatchesContent}
      {topMatchesContent ? (
        <div className={"card-view--top-matches-container"}>
          <h3 className={"card-view--top-matches-container_header"}>
            {bestRemainingResultsLabel}
          </h3>
        </div>
      ) : null}
      <InfiniteScrollWrapper
        className={classnames("card-view", {
          "card-view-horizontal": showAsList,
          "card-view_responsive": hasResponsiveGrid,
        })}
        onLoadMore={props.onLoadMore}
        hasMore={events.collection.length != events.totalRows}
        loading={loading}
        embedded={props.embedded}
      >
        {cardEvents}
        {loadingIndicator}
      </InfiniteScrollWrapper>
      {watermark}
    </div>
  );
};

CardView.propTypes = {
  calendar: PropTypes.object,
  events: PropTypes.object,
  onSelectedDateChange: PropTypes.func.isRequired,
  onUpcomingNavigationChange: PropTypes.func.isRequired,
  selectedDate: PropTypes.object,
  initialized: PropTypes.bool,
  embedded: PropTypes.bool,
  previewType: PropTypes.oneOf(Object.values(PT)),
  onLoadMore: PropTypes.func.isRequired,
  onChangeFilter: PropTypes.func.isRequired,
  selectedFilters: PropTypes.object.isRequired,
  onKeywordSearch: PropTypes.func,
};

export default CardView;
